import { useRef, useState } from 'react';
import { ImagePlus, Paperclip, Ban } from 'lucide-react';
import {
  stripImageMetadata, uploadImage, uploadFile,
  MAX_IMAGE_BYTES, MAX_FILE_BYTES,
} from '@/lib/upload';
import { getBlossomServer, blossomUpload } from '@/lib/blossom';
import { useStore } from '@/relatos/store';
import { finalizeEvent } from 'nostr-tools';
import { hexToBytes } from '@noble/hashes/utils.js';
import type { EventTemplate } from 'nostr-tools';

const SHX_TOKEN_KEY = 'relatos.shxToken';

/**
 * Upload buttons for the BBCode composer. Images are metadata-stripped
 * (canvas re-encode / GIF block splice) before leaving the device; other
 * files are sent as-is with an honest notice — there is no generic
 * metadata stripper.
 *
 * Upload policy (review: "kill uploads in public boards entirely"):
 * - Public boards: uploads DISABLED. Public-board posts and their file
 *   references ride unmoderated public relays; we don't add blob hosting
 *   on top of that exposure. Paste an external link if you must.
 * - Private boards with a Blossom server configured: BUD-02 upload with a
 *   BUD-01 auth event signed by the per-board PSEUDONYMOUS key, so the blob
 *   server can't learn the member identity either.
 * - Private boards without Blossom: legacy third-party hosts (shx.gg /
 *   userdrive) with the existing honest warnings.
 */
export function UploadButtons({ onInsert, boardId }: { onInsert: (snippet: string) => void; boardId: string }) {
  const { publicBoards, boards } = useStore();
  const imgRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [needToken, setNeedToken] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(SHX_TOKEN_KEY) ?? '');
  const pendingImg = useRef<Blob | null>(null);
  const pendingName = useRef('image.png');

  const isPublic = !!publicBoards[boardId];
  const blossom = getBlossomServer();
  const pseudoSk = boards[boardId]?.pseudoSk ?? null;
  const authSign = (tmpl: EventTemplate) => {
    if (!pseudoSk) throw new Error('No board posting key yet');
    return Promise.resolve(finalizeEvent(tmpl, hexToBytes(pseudoSk)));
  };

  if (isPublic) {
    return (
      <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}
            title="Uploads are disabled on public boards — public posts already ride unmoderated public relays">
        <button type="button" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
          <Ban size={11} aria-hidden style={{ verticalAlign: -1 }} /> No uploads on public boards
        </button>
      </span>
    );
  }

  const doImage = async (blob: Blob, name: string, tok: string) => {
    setStatus('Uploading image…');
    let url: string;
    if (blossom && pseudoSk) {
      url = await blossomUpload(blossom, blob, name, authSign);
    } else {
      url = await uploadImage(blob, name, tok || undefined);
    }
    onInsert(`[img]${url}[/img]`);
    setStatus(null);
  };

  const pickImage = async (f: File) => {
    setErr(null);
    if (f.size > MAX_IMAGE_BYTES) { setErr('Image too large (max 10 MB)'); return; }
    try {
      setStatus('Stripping metadata…');
      const clean = await stripImageMetadata(f);
      const name = f.name.replace(/\.[a-z0-9]+$/i, '') + (clean.type === 'image/jpeg' ? '.jpg' : clean.type === 'image/webp' ? '.webp' : clean.type === 'image/gif' ? '.gif' : '.png');
      pendingImg.current = clean; pendingName.current = name;
      await doImage(clean, name, token);
    } catch (e) {
      setStatus(null);
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      if (/403|401|token/i.test(msg)) setNeedToken(true);
    }
  };

  const retryWithToken = async () => {
    if (!pendingImg.current) return;
    setErr(null);
    localStorage.setItem(SHX_TOKEN_KEY, token);
    try { await doImage(pendingImg.current, pendingName.current, token); setNeedToken(false); }
    catch (e) { setStatus(null); setErr(e instanceof Error ? e.message : String(e)); }
  };

  const pickFile = async (f: File) => {
    setErr(null);
    if (f.size > MAX_FILE_BYTES) { setErr('File too large (max 50 MB)'); return; }
    try {
      setStatus('Uploading file…');
      let url: string;
      if (blossom && pseudoSk) {
        url = await blossomUpload(blossom, f, f.name, authSign);
      } else {
        url = await uploadFile(f, f.name);
      }
      onInsert(`[url=${url}]${f.name}[/url]`);
      setStatus(null);
    } catch (e) { setStatus(null); setErr(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
      <button type="button" title={blossom ? 'Upload image via Blossom (metadata stripped)' : 'Upload image (metadata stripped)'}
              onClick={() => imgRef.current?.click()}>
        <ImagePlus size={11} aria-hidden style={{ verticalAlign: -1 }} /> Img+
      </button>
      <button type="button" title={blossom ? 'Upload file via Blossom (sent as-is)' : 'Upload file (sent as-is)'}
              onClick={() => fileRef.current?.click()}>
        <Paperclip size={11} aria-hidden style={{ verticalAlign: -1 }} /> File+
      </button>
      <input ref={imgRef} type="file" accept="image/*" hidden
             onChange={e => { const f = e.target.files?.[0]; if (f) pickImage(f); e.target.value = ''; }} />
      <input ref={fileRef} type="file" hidden
             onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = ''; }} />
      {status && <span className="rl-small">{status}</span>}
      {err && <span style={{ color: 'var(--rl-bad)', fontSize: 11 }}>{err}</span>}
      {needToken && (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input
            type="password" className="rl-input rl-mono" style={{ width: 220, padding: '2px 6px', fontSize: 11 }}
            placeholder="shx API token" value={token} aria-label="shx API token"
            onChange={e => setToken(e.target.value)} />
          <button type="button" className="rl-btn rl-btn-sm" onClick={retryWithToken}>Retry</button>
        </span>
      )}
    </span>
  );
}
